import React, { useState, useEffect, useRef, useCallback } from "react";
import "./AdminPanel.css";

// ✅ CORRECT BACKEND URL (LIVE)
const BACKEND_URL = "https://www.thehexaplay.com";
const API = `${BACKEND_URL}/api/admin`;

/* ── Image URL Helper ── */
const getImageUrl = (imagePath) => {
  if (!imagePath) return "";
  // If it's already a full URL (CDN, S3, or DataURI), return as is
  if (imagePath.startsWith("data:") || imagePath.startsWith("http")) return imagePath;

  // Otherwise, prefix with our live backend domain
  const cleanPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
  return `${BACKEND_URL}${cleanPath}`;
};

const TABS = [
  { key: "occasions", label: "Occasions", icon: "🎉", color: "#6366f1" },
  { key: "cakes", label: "Cakes", icon: "🎂", color: "#ec4899" },
  { key: "decorations", label: "Decorations", icon: "✨", color: "#f59e0b" },
  { key: "gifts", label: "Gifts", icon: "🎁", color: "#22c55e" },
  { key: "services", label: "Services", icon: "📸", color: "#3b82f6" },
];

export default function AdminPanel() {
  const [tab, setTab] = useState("occasions");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [counts, setCounts] = useState({});

  const [formName, setFormName] = useState("");
  const [formPrice, setFormPrice] = useState("");
  const [formFile, setFormFile] = useState(null);
  const [preview, setPreview] = useState("");

  const fileRef = useRef(null);
  const toastId = useRef(0);

  /* ── Fetch Items ── */
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/${tab}`, {
        credentials: "include",
      });

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Unauthorized - please login");
        } else if (res.status === 403) {
          throw new Error("Forbidden - check CORS permissions");
        } else {
          throw new Error(`Failed to fetch (${res.status})`);
        }
      }

      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
      setCounts((prev) => ({ ...prev, [tab]: Array.isArray(data) ? data.length : 0 }));
    } catch (err) {
      console.error("Fetch error:", err);
      toast(err.message, "error");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  /* ── Fetch all counts for badges ── */
  useEffect(() => {
    const fetchCounts = async () => {
      const promises = TABS.map((t) =>
        fetch(`${API}/${t.key}`, { credentials: "include" })
          .then((r) => (r.ok ? r.json() : []))
          .then((d) => ({ key: t.key, count: Array.isArray(d) ? d.length : 0 }))
          .catch(() => ({ key: t.key, count: 0 }))
      );
      const results = await Promise.all(promises);
      const newCounts = {};
      results.forEach((r) => (newCounts[r.key] = r.count));
      setCounts(newCounts);
    };
    fetchCounts();
  }, [modal, deleting]);

  /* ── Toast System ── */
  const toast = (msg, type = "success") => {
    const id = ++toastId.current;
    setToasts((prev) => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3500);
  };

  /* ── Modal Handlers ── */
  const openAdd = () => {
    setEditing(null);
    setFormName("");
    setFormPrice("");
    setFormFile(null);
    setPreview("");
    setModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setFormName(item.name);
    setFormPrice(String(item.price || ""));
    setFormFile(null);
    setPreview(getImageUrl(item.image) || "");
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
    setEditing(null);
  };

  /* ── File Handling ── */
  const handleFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setFormFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove("ap-dragover");
    const f = e.dataTransfer.files && e.dataTransfer.files[0];
    if (f && f.type.startsWith("image/")) {
      setFormFile(f);
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(f);
    }
  };

  /* ── Form Submit (Add/Edit) ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formName.trim()) {
      toast("Name is required", "error");
      return;
    }
    if (tab !== "occasions" && (!formPrice || isNaN(formPrice) || Number(formPrice) <= 0)) {
      toast("Valid price is required", "error");
      return;
    }

    setSaving(true);
    const fd = new FormData();
    fd.append("name", formName.trim());
    fd.append("price", formPrice || "0");
    if (formFile) {
      fd.append("image", formFile);
    }

    try {
      // ✅ FIXED ENDPOINTS (per your update)
      const url = editing
        ? `${API}/${tab}/${editing._id}`
        : `${API}/${tab}`;
      const method = editing ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        body: fd,
        credentials: "include",
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || `Failed to save (${res.status})`);
      }

      toast(editing ? "Updated successfully!" : "Added successfully!");
      closeModal();
      fetchItems();
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete Handler ── */
  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      // ✅ FIXED ENDPOINT
      const res = await fetch(`${API}/${tab}/${deleting._id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Delete failed");
      toast("Deleted successfully!");
      setDeleting(null);
      fetchItems();
    } catch (err) {
      toast(err.message, "error");
    }
  };

  /* ── Derived ── */
  const filtered = items.filter((i) =>
    i.name.toLowerCase().includes(search.toLowerCase())
  );
  const activeTabData = TABS.find((t) => t.key === tab) || TABS[0];
  const singular = activeTabData.label.replace(/s$/, "");

  return (
    <div className="ap">
      <header className="ap-header">
        <div className="ap-header-left">
          <div className="ap-logo">
            <span className="ap-logo-icon">⚡</span>
            <div>
              <h1 className="ap-title">Event Admin</h1>
              <p className="ap-subtitle">Manage your event catalog</p>
            </div>
          </div>
        </div>
        <div className="ap-header-stats">
          {TABS.map((t) => (
            <div
              key={t.key}
              className={`ap-mini-stat ${tab === t.key ? "active" : ""}`}
              onClick={() => { setTab(t.key); setSearch(""); }}
            >
              <span className="ap-mini-icon">{t.icon}</span>
              <span className="ap-mini-count">{counts[t.key] || 0}</span>
            </div>
          ))}
        </div>
      </header>

      <nav className="ap-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`ap-tab ${tab === t.key ? "active" : ""}`}
            onClick={() => { setTab(t.key); setSearch(""); }}
            style={tab === t.key ? { "--tab-color": t.color } : {}}
          >
            <span className="ap-tab-icon">{t.icon}</span>
            <span className="ap-tab-label">{t.label}</span>
            {counts[t.key] > 0 && <span className="ap-tab-badge">{counts[t.key]}</span>}
          </button>
        ))}
      </nav>

      <div className="ap-toolbar">
        <div className="ap-toolbar-left">
          <h2 className="ap-section-title">{activeTabData.icon} {activeTabData.label}</h2>
          <span className="ap-item-count">
            {filtered.length} item{filtered.length !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="ap-toolbar-right">
          <div className="ap-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              placeholder={`Search ${activeTabData.label.toLowerCase()}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="ap-search-clear" onClick={() => setSearch("")}>×</button>
            )}
          </div>
          <button className="ap-add-btn" onClick={openAdd}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>Add {singular}</span>
          </button>
        </div>
      </div>

      <main className="ap-main">
        {loading ? (
          <div className="ap-grid">
            {[1,2,3,4].map((i) => <div key={i} className="ap-skeleton" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="ap-empty">
            <div className="ap-empty-visual">
              <div className="ap-empty-circle"><span>{activeTabData.icon}</span></div>
            </div>
            <h3>{search ? "No Results Found" : `No ${activeTabData.label} Yet`}</h3>
            <p>
              {search
                ? `No ${activeTabData.label.toLowerCase()} match "${search}"`
                : `Click the button below to add your first ${singular.toLowerCase()}`}
            </p>
            {!search && (
              <button className="ap-add-btn" onClick={openAdd}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add {singular}
              </button>
            )}
          </div>
        ) : (
          <div className="ap-grid">
            {filtered.map((item, idx) => (
              <div key={item._id} className="ap-card" style={{ animationDelay: `${idx * 0.06}s` }}>
                <div className="ap-card-visual">
                  {item.image ? (
                    <img
                      src={getImageUrl(item.image)}
                      alt={item.name}
                      className="ap-card-img"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="ap-card-placeholder"><span>{activeTabData.icon}</span></div>
                  )}
                  <div className="ap-card-actions">
                    <button className="ap-action-btn ap-edit-btn" onClick={() => openEdit(item)} title="Edit">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button className="ap-action-btn ap-delete-btn" onClick={() => setDeleting(item)} title="Delete">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </div>
                  {item.price > 0 && <div className="ap-card-price-tag">₹{item.price}</div>}
                </div>
                <div className="ap-card-info">
                  <h4 className="ap-card-name">{item.name}</h4>
                  <span className="ap-card-date">
                    {new Date(item.createdAt).toLocaleDateString("en-IN", {
                      day: "numeric", month: "short",
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {modal && (
        <div className="ap-overlay" onClick={closeModal}>
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ap-modal-head">
              <div>
                <h3 className="ap-modal-title">{editing ? "Edit" : "Add New"} {singular}</h3>
                <p className="ap-modal-desc">
                  {editing ? "Update the details below" : `Fill in the details for the new ${singular.toLowerCase()}`}
                </p>
              </div>
              <button className="ap-modal-close" onClick={closeModal}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="ap-modal-body">
              <div
                className={`ap-upload-zone ${preview ? "has-img" : ""}`}
                onClick={() => fileRef.current && fileRef.current.click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("ap-dragover"); }}
                onDragLeave={(e) => e.currentTarget.classList.remove("ap-dragover")}
                onDrop={handleDrop}
              >
                {preview ? (
                  <div className="ap-upload-preview-wrap">
                    <img src={preview} alt="Preview" className="ap-upload-img" />
                    <div className="ap-upload-overlay-text">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      Change Image
                    </div>
                  </div>
                ) : (
                  <div className="ap-upload-empty">
                    <div className="ap-upload-icon-wrap">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <path d="m21 15-5-5L5 21" />
                      </svg>
                    </div>
                    <p className="ap-upload-text">Drop image here or <span>browse</span></p>
                    <p className="ap-upload-hint">PNG, JPG up to 10MB</p>
                  </div>
                )}
                <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} hidden />
              </div>

              <div className="ap-field">
                <label className="ap-label">Name <span className="ap-required">*</span></label>
                <input
                  className="ap-input"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder={`Enter ${singular.toLowerCase()} name`}
                  autoFocus
                />
              </div>

              <div className="ap-field">
                <label className="ap-label">
                  Price (₹)
                  {tab === "occasions" && <span className="ap-optional">optional</span>}
                </label>
                <input
                  className="ap-input"
                  type="number"
                  min="0"
                  value={formPrice}
                  onChange={(e) => setFormPrice(e.target.value)}
                  placeholder="0"
                />
              </div>

              <div className="ap-modal-btns">
                <button type="button" className="ap-btn ap-btn-ghost" onClick={closeModal}>Cancel</button>
                <button type="submit" className="ap-btn ap-btn-primary" disabled={saving}>
                  {saving ? (
                    <span className="ap-spinner" />
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {editing ? "Update" : "Save"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleting && (
        <div className="ap-overlay" onClick={() => setDeleting(null)}>
          <div className="ap-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="ap-confirm-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h3 className="ap-confirm-title">Delete "{deleting.name}"?</h3>
            <p className="ap-confirm-text">
              This item will be removed from all listings. This action cannot be undone.
            </p>
            <div className="ap-confirm-btns">
              <button className="ap-btn ap-btn-ghost" onClick={() => setDeleting(null)}>Cancel</button>
              <button className="ap-btn ap-btn-danger" onClick={confirmDelete}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="ap-toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`ap-toast ap-toast-${t.type}`}>
            <span className="ap-toast-icon">{t.type === "success" ? "✅" : "❌"}</span>
            <span className="ap-toast-msg">{t.msg}</span>
          </div>
        ))}
      </div>
    </div>
  );
}